import { formatDate, formatMoney } from '../../lib/datetime';
import {
  cadenceText,
  commitmentStatusLabel,
  completionLine,
  nextContributionDate,
} from '../../lib/commitments.helpers';
import { colors } from '../../styles/theme';
import type { Commitment } from '../../types/commitments';

function statusTone(status: string): { bg: string; fg: string } {
  switch (status) {
    case 'active':
      return { bg: colors.successBg, fg: colors.success };
    case 'paused':
    case 'pending_cancellation':
      return { bg: colors.warningBg, fg: colors.warning };
    case 'cancelled':
    case 'terminated':
      return { bg: colors.dangerBg, fg: colors.danger };
    default:
      return { bg: colors.canvas, fg: colors.muted };
  }
}

/**
 * Reciprocal of `MyCommitmentsCard`'s "View contract" link: shown on the
 * client's contract page once a commitment exists for this case, so a member
 * who just signed (or got approved) sees "your commitment is live" — cadence,
 * funding amount, progress, and the next expected contribution — without
 * hunting for it in Holdings. Client vocabulary: "contribution"
 * (never "deposit"); progress is count-only (no streaks / loss-aversion).
 */
export default function ContractCommitmentCard({
  commitment,
}: {
  readonly commitment: Commitment;
}): JSX.Element {
  const c = commitment;
  const tone = statusTone(c.status);
  const next = nextContributionDate(c);
  return (
    <section
      className="rounded-2xl p-6 mb-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      aria-label="Your contribution commitment"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
            Your contribution commitment
          </h2>
          <p className="text-sm" style={{ color: colors.muted }}>{c.product_name}</p>
        </div>
        <span
          className="inline-block px-2 py-0.5 rounded text-xs font-medium"
          style={{ background: tone.bg, color: tone.fg }}
        >
          {commitmentStatusLabel(c.status)}
        </span>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
        <div>
          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.mutedSoft }}>Contribution</dt>
          <dd className="text-sm font-medium mt-0.5" style={{ color: colors.ink }}>
            {formatMoney(c.amount, c.currency_code)}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.mutedSoft }}>Cadence</dt>
          <dd className="text-sm font-medium mt-0.5" style={{ color: colors.ink }}>{cadenceText(c)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.mutedSoft }}>Progress</dt>
          <dd className="text-sm font-medium mt-0.5" style={{ color: colors.ink }}>{completionLine(c)}</dd>
        </div>
        {next && (
          <div>
            <dt className="text-xs uppercase tracking-wide" style={{ color: colors.mutedSoft }}>
              Next contribution (est.)
            </dt>
            <dd className="text-sm font-medium mt-0.5" style={{ color: colors.ink }}>{formatDate(next)}</dd>
          </div>
        )}
      </dl>

      <p className="text-xs mt-4" style={{ color: colors.mutedSoft }}>
        Manage this commitment from your{' '}
        <a href="/client/portfolio" className="underline" style={{ color: colors.navy }}>holdings</a>.
      </p>
    </section>
  );
}
